/* Claude theme · brand motion & 3D interactions (vanilla, no deps) */
(() => {
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  root.classList.add('ch-js');

  /* ---------- Scroll progress bar ---------- */
  const bar = document.createElement('div');
  bar.className = 'ch-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.setProperty('--p', h > 0 ? (window.scrollY / h).toFixed(4) : 0);
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- Reveal on scroll ---------- */
  const isHome = document.body.classList.contains('index-template') || location.pathname === '/' || /^\/[a-z]{2}(-[a-z]{2})?\/?$/i.test(location.pathname);
  if (isHome && !reduce) {
    document.querySelectorAll('#MainContent > .shopify-section').forEach((s, i) => {
      if (i > 0 && !s.querySelector('.ch-hero')) s.classList.add('ch-section-reveal');
    });
  }
  const revealTargets = document.querySelectorAll('.ch-reveal, .ch-section-reveal');
  if ('IntersectionObserver' in window && !reduce) {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    revealTargets.forEach((el) => io.observe(el));
  } else {
    revealTargets.forEach((el) => el.classList.add('is-in'));
  }

  /* ---------- Hero 3D parallax ---------- */
  document.querySelectorAll('[data-ch-parallax]').forEach((hero) => {
    const scene = hero.querySelector('[data-ch-scene]');
    const copy = hero.querySelector('.ch-hero__copy');
    const gloss = hero.querySelector('.ch-frame__gloss');
    if (!scene || reduce) return;
    let raf = null;
    const set = (x, y) => {
      scene.style.setProperty('--ry', (x * 14).toFixed(2) + 'deg');
      scene.style.setProperty('--rx', (-y * 10).toFixed(2) + 'deg');
      if (copy) {
        copy.style.setProperty('--tx', (x * -10).toFixed(1) + 'px');
        copy.style.setProperty('--ty', (y * -6).toFixed(1) + 'px');
      }
      if (gloss) {
        gloss.style.setProperty('--gx', (50 + x * 35).toFixed(0) + '%');
        gloss.style.setProperty('--gy', (40 + y * 35).toFixed(0) + '%');
      }
    };
    if (finePointer) {
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 2 - 1;
        const y = ((e.clientY - r.top) / r.height) * 2 - 1;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => set(x, y));
      });
      hero.addEventListener('pointerleave', () => set(0, 0));
    } else {
      let t = 0;
      let running = false;
      const loop = () => {
        if (!running) return;
        t += 0.012;
        set(Math.sin(t) * 0.6, Math.cos(t * 0.8) * 0.4);
        requestAnimationFrame(loop);
      };
      new IntersectionObserver((en) => {
        const vis = en[0].isIntersecting;
        if (vis && !running) { running = true; loop(); }
        if (!vis) running = false;
      }).observe(hero);
    }
  });

  /* ---------- Magnetic buttons ---------- */
  if (finePointer && !reduce) {
    document.querySelectorAll('[data-ch-magnetic]').forEach((btn) => {
      btn.addEventListener('pointermove', (e) => {
        const r = btn.getBoundingClientRect();
        btn.style.setProperty('--mx', ((e.clientX - r.left - r.width / 2) * 0.25).toFixed(1) + 'px');
        btn.style.setProperty('--my', ((e.clientY - r.top - r.height / 2) * 0.35).toFixed(1) + 'px');
      });
      btn.addEventListener('pointerleave', () => {
        btn.style.setProperty('--mx', '0px');
        btn.style.setProperty('--my', '0px');
      });
    });
  }

  /* ---------- 3D tilt on product & collection card images ---------- */
  const initTilt = (scope) => {
    if (!finePointer || reduce) return;
    scope.querySelectorAll('.card-wrapper .card__inner').forEach((el) => {
      if (el.dataset.chTilt !== undefined) return;
      el.setAttribute('data-ch-tilt', '');
      let raf = null;
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.classList.add('is-tilting');
          el.style.setProperty('--tilt-y', ((px - 0.5) * 12).toFixed(2) + 'deg');
          el.style.setProperty('--tilt-x', ((0.5 - py) * 12).toFixed(2) + 'deg');
          el.style.setProperty('--glare-x', (px * 100).toFixed(0) + '%');
          el.style.setProperty('--glare-y', (py * 100).toFixed(0) + '%');
        });
      });
      el.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf);
        el.classList.remove('is-tilting');
        el.style.setProperty('--tilt-x', '0deg');
        el.style.setProperty('--tilt-y', '0deg');
      });
    });
  };
  initTilt(document);
  if ('MutationObserver' in window && finePointer && !reduce) {
    let mt = null;
    new MutationObserver(() => {
      clearTimeout(mt);
      mt = setTimeout(() => initTilt(document), 250);
    }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
  }

  /* ---------- 3D category carousel ---------- */
  document.querySelectorAll('[data-ch-carousel]').forEach((vp) => {
    const ring = vp.querySelector('[data-ch-ring]');
    const cards = [...vp.querySelectorAll('.ch-carousel__card')];
    const section = vp.closest('section');
    const n = cards.length;
    if (!ring || n === 0) return;
    const mq = window.matchMedia('(max-width: 749px)');
    const step = 360 / n;
    let index = 0;
    let timer = null;

    const layout = () => {
      if (mq.matches) return;
      const w = cards[0].offsetWidth || 280;
      const r = Math.round(w / 2 / Math.tan(Math.PI / Math.max(n, 3)) + 60);
      vp.style.setProperty('--r', r + 'px');
    };
    const go = (i) => {
      index = i;
      vp.style.setProperty('--angle', (-index * step) + 'deg');
      const front = ((index % n) + n) % n;
      cards.forEach((c, k) => {
        c.classList.toggle('is-front', k === front);
        if (!mq.matches) c.setAttribute('tabindex', k === front ? '0' : '-1');
        else c.removeAttribute('tabindex');
      });
    };
    const stop = () => clearInterval(timer);
    const play = () => {
      if (reduce || mq.matches) return;
      stop();
      timer = setInterval(() => go(index + 1), 3800);
    };

    const nextBtn = section && section.querySelector('[data-ch-next]');
    const prevBtn = section && section.querySelector('[data-ch-prev]');
    if (nextBtn) nextBtn.addEventListener('click', () => { go(index + 1); play(); });
    if (prevBtn) prevBtn.addEventListener('click', () => { go(index - 1); play(); });
    vp.addEventListener('pointerenter', stop);
    vp.addEventListener('pointerleave', play);
    vp.addEventListener('focusin', stop);
    vp.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { go(index + 1); cards[((index % n) + n) % n].focus(); }
      if (e.key === 'ArrowLeft') { go(index - 1); cards[((index % n) + n) % n].focus(); }
    });

    let startX = null, startAngle = 0, moved = false;
    vp.addEventListener('pointerdown', (e) => {
      if (mq.matches || e.button !== 0) return;
      startX = e.clientX; startAngle = -index * step; moved = false;
      vp.classList.add('is-dragging'); stop();
    });
    window.addEventListener('pointermove', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      vp.style.setProperty('--angle', (startAngle + dx * 0.35) + 'deg');
    });
    window.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      vp.classList.remove('is-dragging');
      go(index - Math.round((dx * 0.35) / step));
      play();
    });
    vp.addEventListener('click', (e) => { if (moved) { e.preventDefault(); moved = false; } }, true);
    vp.addEventListener('dragstart', (e) => e.preventDefault());

    const onResize = () => { layout(); go(index); };
    window.addEventListener('resize', onResize);
    if (mq.addEventListener) mq.addEventListener('change', () => { onResize(); if (mq.matches) stop(); else play(); });
    layout(); go(0);

    new IntersectionObserver((en) => (en[0].isIntersecting ? play() : stop()), { threshold: 0.2 }).observe(vp);
  });

  /* ---------- Flip cards: tap to flip on touch ---------- */
  if (!finePointer) {
    document.querySelectorAll('.ch-flip__card').forEach((c) => {
      c.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;
        c.classList.toggle('is-flipped');
      });
    });
  }

  /* ---------- Sticky add-to-cart (product pages) ---------- */
  const atcBar = document.querySelector('[data-ch-sticky-atc]');
  const mainForm = document.querySelector('.product__info-container .product-form__submit');
  if (atcBar && mainForm && 'IntersectionObserver' in window) {
    new IntersectionObserver((en) => {
      const past = !en[0].isIntersecting && en[0].boundingClientRect.top < 0;
      atcBar.classList.toggle('is-on', past);
    }).observe(mainForm);
    atcBar.querySelector('button').addEventListener('click', () => {
      mainForm.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      if (!mainForm.disabled) setTimeout(() => mainForm.click(), reduce ? 0 : 450);
    });
  }
})();
